import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useThemeUI, ThemeProvider, merge } from '@theme-ui/core';
import React, { useState, useEffect } from 'react';
import { toCustomProperties } from './custom-properties.esm.js';
import { memoizedGet } from '../utils/object.esm.js';
import getBorders from '../themes/theme-keys/borders.esm.js';
import getGradients from '../themes/theme-keys/gradients.esm.js';
import getShadows from '../themes/theme-keys/shadows.esm.js';

var STORAGE_KEY = 'csg-tui-color-mode';
var storage = {
  get: function get() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('localStorage is disabled and color mode might not work as expected.', 'Please check your Site Settings.', e);
    }
    return;
  },
  set: function set(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('localStorage is disabled and color mode might not work as expected.', 'Please check your Site Settings.', e);
    }
  }
};
var getPreferredColorScheme = function getPreferredColorScheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  }
  return null;
};
var getModeFromClass = function getModeFromClass() {
  var mode;
  if (typeof document !== 'undefined') {
    document.documentElement.classList.forEach(function (className) {
      if (className.startsWith('csg-tui-')) {
        mode = className.replace('csg-tui-', '');
      }
    });
  }
  return mode;
};
var useColorModeState = function useColorModeState() {
  var theme = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // eslint-disable-next-line prefer-const
  var _useState = useState(function () {
      var modeFromClass = getModeFromClass();
      if (modeFromClass) {
        return modeFromClass;
      }
      var preferredMode = theme.useColorSchemeMediaQuery !== false && getPreferredColorScheme();
      return preferredMode || theme.initialColorModeName;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    mode = _useState2[0],
    setMode = _useState2[1];

  // on first render, we read the color mode from localStorage and
  // clear the class on document element body
  useEffect(function () {
    var stored = theme.useLocalStorage !== false && storage.get();
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('cgs-tui-' + stored);
      document.body.classList.remove('cgs-tui-' + stored);
    }
    if (stored && stored !== mode) {
      setMode(stored);
    }
  }, [mode, theme.useLocalStorage]);

  // when mode changes, we save it to localStorage
  useEffect(function () {
    if (mode && theme.useLocalStorage !== false) {
      storage.set(mode);
    }
  }, [mode, theme.useLocalStorage]);
  if (process.env.NODE_ENV !== 'production') {
    if (theme.colors && theme.colors.modes && theme.initialColorModeName && Object.keys(theme.colors.modes).indexOf(theme.initialColorModeName) > -1) {
      // eslint-disable-next-line no-console
      console.warn('The `initialColorModeName` value should be a unique name' + ' and cannot reference a key in `theme.colors.modes`.');
    }
  }
  return [mode, setMode];
};
var applyColorMode = function applyColorMode(theme, mode) {
  if (!mode) return _objectSpread2({}, theme);
  var modes = memoizedGet(theme, 'colors.modes', {});
  var colorSchema = memoizedGet(modes, mode, {});
  return merge.all({}, theme, {
    colors: colorSchema,
    borders: getBorders(colorSchema),
    gradients: getGradients(colorSchema),
    shadows: getShadows(colorSchema)
  });
};
var ColorModeProvider = function ColorModeProvider(_ref2) {
  var children = _ref2.children;
  var outer = useThemeUI();
  var _useColorModeState = useColorModeState(outer.theme),
    _useColorModeState2 = _slicedToArray(_useColorModeState, 2),
    colorMode = _useColorModeState2[0],
    setColorMode = _useColorModeState2[1];
  var theme = applyColorMode(outer.theme || {}, colorMode);
  if (theme.useCustomProperties !== false) {
    // TODO: This mutation is less than ideal
    // We could save custom properties to `theme.colorVars`,
    // But it's infeasible to do this because of how the packages are split.
    theme.rawColors = theme.colors;
    theme.colors = toCustomProperties(theme.colors, 'colors');
  }
  var context = _objectSpread2(_objectSpread2({}, outer), {}, {
    theme: theme,
    colorMode: colorMode,
    setColorMode: setColorMode
  });

  // const isTopLevelColorModeProvider = outer.setColorMode === undefined

  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: context
  }, children);
};

export default ColorModeProvider;
//# sourceMappingURL=color-mode-provider.esm.js.map
